"use client"

import { useState, useEffect } from "react"
import {
  Layout,
  Menu,
  Avatar,
  Button,
  message,
  Modal,
  Space,
  Spin,
  Dropdown,
  Typography,
  Drawer,
  Grid
} from "antd"
import {
  UserOutlined,
  LogoutOutlined,
  DashboardOutlined,
  UsergroupAddOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  TruckOutlined,
  BankOutlined,
  CarryOutOutlined,
  SettingOutlined,
  DatabaseOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LeftOutlined,
  MenuOutlined,
  CreditCardOutlined,
  EyeOutlined,
  SyncOutlined,
  KeyOutlined,
  FileTextOutlined,
  MailOutlined,
  LineChartOutlined,
  FilePdfOutlined
} from "@ant-design/icons"
import { useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { motion } from "framer-motion"

const { Sider, Content, Header } = Layout
const { confirm } = Modal
const { Text } = Typography
const { useBreakpoint } = Grid

const AdminLayout = ({ children }) => {
  const screens = useBreakpoint()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isAuthenticated, isAuthChecked, loading: authLoading, checkAuth } = useAuth()
  const [collapsed, setCollapsed] = useState(false)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [loading, setLoading] = useState(true)
  const [openKeys, setOpenKeys] = useState([])

  // Determine if we should show sidebar (desktop) or drawer (mobile)
  const showSidebar = screens.lg
  const showDrawer = !screens.lg
  const sidebarWidth = showSidebar ? 200 : 0

  useEffect(() => {
    if (isAuthChecked && !authLoading) {
      if (!isAuthenticated || user?.role !== 'admin') {
        navigate('/login')
        return
      }
      setLoading(false)
    }
  }, [isAuthenticated, isAuthChecked, authLoading, user?.role, navigate])

  // Auto-collapse on smaller screens
  useEffect(() => {
    if (showDrawer) {
      setCollapsed(false) // Reset collapse state on mobile
    }
  }, [screens.lg])

  // Set open keys when location changes
  useEffect(() => {
    const { openKeys: defaultOpenKeys } = getCurrentMenuKey();
    setOpenKeys(defaultOpenKeys);
  }, [location.pathname])

  // Get current menu key and open keys for highlighting nested menus
  const getCurrentMenuKey = () => {
    const path = location.pathname;

    // Handle exact matches first
    if (path === "/admin/dashboard") return { key: "dashboard", openKeys: [] };
    if (path === "/admin/users") return { key: "users", openKeys: ["management"] };
    if (path === "/admin/products") return { key: "products", openKeys: ["management"] };
    if (path === "/admin/categories") return { key: "categories", openKeys: ["management"] };
    if (path === "/admin/navigation") return { key: "navigation", openKeys: ["management"] };
    if (path === "/admin/providers") return { key: "providers", openKeys: ["management"] };
    if (path === "/admin/orders") return { key: "orders", openKeys: [] };
    if (path === "/admin/transactions") return { key: "transactions", openKeys: [] };
    if (path === "/admin/settings") return { key: "settings", openKeys: ["system"] };
    if (path === "/admin/backups") return { key: "backups", openKeys: ["system"] };
    if (path === "/admin/saved-cards") return { key: "saved-cards", openKeys: ["management"] };
    if (path === "/admin/recurring-payments") return { key: "recurring-payments", openKeys: ["management"] };
    if (path === "/admin/impersonation-log") return { key: "impersonation-log", openKeys: ["management"] };
    if (path === "/admin/api-keys") return { key: "api-keys", openKeys: ["system"] };

    // Handle nested routes (check for parent paths)
    if (path.includes('/admin/users/')) return { key: "users", openKeys: ["management"] };
    if (path.includes('/admin/products/')) return { key: "products", openKeys: ["management"] };
    if (path.includes('/admin/categories/')) return { key: "categories", openKeys: ["management"] };
    if (path.includes('/admin/providers/')) return { key: "providers", openKeys: ["management"] };

    return { key: "", openKeys: [] };
  }

  const menuItems = [
    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
      onClick: () => {
        navigate("/admin/dashboard")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "management",
      icon: <AppstoreOutlined />,
      label: "Management",
      children: [
        {
          key: "users",
          icon: <UsergroupAddOutlined />,
          label: "Users",
          onClick: () => {
            navigate("/admin/users")
            if (showDrawer) setDrawerVisible(false)
          }
        },
        {
          key: "products",
          icon: <ShoppingOutlined />,
          label: "Products",
          onClick: () => {
            navigate("/admin/products")
            if (showDrawer) setDrawerVisible(false)
          }
        },
        {
          key: "categories",
          icon: <AppstoreOutlined />,
          label: "Categories",
          onClick: () => {
            navigate("/admin/categories")
            if (showDrawer) setDrawerVisible(false)
          }
        },
        {
          key: "navigation",
          icon: <AppstoreOutlined />,
          label: "Storefront navigation",
          onClick: () => {
            navigate("/admin/navigation")
            if (showDrawer) setDrawerVisible(false)
          }
        },
        {
          key: "providers",
          icon: <TruckOutlined />,
          label: "Providers",
          onClick: () => {
            navigate("/admin/providers")
            if (showDrawer) setDrawerVisible(false)
          }
        },
        {
          key: "saved-cards",
          icon: <CreditCardOutlined />,
          label: "Saved Cards",
          onClick: () => {
            navigate("/admin/saved-cards")
            if (showDrawer) setDrawerVisible(false)
          }
        },
        {
          key: "recurring-payments",
          icon: <SyncOutlined />,
          label: "Recurring",
          onClick: () => {
            navigate("/admin/recurring-payments")
            if (showDrawer) setDrawerVisible(false)
          }
        },
        {
          key: "impersonation-log",
          icon: <EyeOutlined />,
          label: "Impersonation Log",
          onClick: () => {
            navigate("/admin/impersonation-log")
            if (showDrawer) setDrawerVisible(false)
          }
        }
      ]
    },
    {
      key: "orders",
      icon: <CarryOutOutlined />,
      label: "Orders",
      onClick: () => {
        navigate("/admin/orders")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "transactions",
      icon: <BankOutlined />,
      label: "Transactions",
      onClick: () => {
        navigate("/admin/transactions")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "blog",
      icon: <FileTextOutlined />,
      label: "Blog",
      onClick: () => {
        navigate("/admin/blog")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "newsletters",
      icon: <MailOutlined />,
      label: "Newsletters",
      onClick: () => {
        navigate("/admin/newsletters")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "reports",
      icon: <FilePdfOutlined />,
      label: "Reports",
      onClick: () => {
        navigate("/admin/reports")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "marketing",
      icon: <LineChartOutlined />,
      label: "Marketing",
      onClick: () => {
        navigate("/admin/marketing")
        if (showDrawer) setDrawerVisible(false)
      }
    },
    {
      key: "system",
      icon: <SettingOutlined />,
      label: "System",
      children: [
        {
          key: "settings",
          icon: <SettingOutlined />,
          label: "Settings",
          onClick: () => {
            navigate("/admin/settings")
            if (showDrawer) setDrawerVisible(false)
          }
        },
        {
          key: "backups",
          icon: <DatabaseOutlined />,
          label: "Backups",
          onClick: () => {
            navigate("/admin/backups")
            if (showDrawer) setDrawerVisible(false)
          }
        },
        {
          key: "api-keys",
          icon: <KeyOutlined />,
          label: "API Keys",
          onClick: () => {
            navigate("/admin/api-keys")
            if (showDrawer) setDrawerVisible(false)
          }
        }
      ]
    }
  ]

  const handleLogout = () => {
    confirm({
      title: 'Confirm Logout',
      content: 'Are you sure you want to sign out?',
      onOk() {
        logout()
        message.success('Logged out successfully')
        navigate('/')
      },
    })
  }

  const toggleCollapsed = () => {
    setCollapsed(!collapsed)
  }

  const SidebarContent = () => (
    <>
      <div style={{
        padding: '16px',
        textAlign: 'center',
        borderBottom: '1px solid #d9d9d9'
      }}>
        <Avatar
          size={48}
          icon={<UserOutlined />}
          style={{ marginBottom: '8px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div style={{ color: '#000000', fontSize: '14px', fontWeight: 'bold' }}>
          {user?.name}
        </div>
        <div style={{ color: '#1890ff', fontSize: '12px', marginTop: '4px' }}>
          Administrator
        </div>
      </div>

      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[getCurrentMenuKey().key]}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        items={menuItems}
        style={{
          border: 'none',
          fontSize: screens.xs ? '14px' : '16px'
        }}
      />

      <div style={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        right: 16
      }}>
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={() => navigate('/account/dashboard')}
          style={{
            width: '100%',
            color: '#000000',
            textAlign: 'left',
            padding: '8px 16px',
            marginBottom: '8px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            background: 'transparent',
            transition: 'all 0.2s'
          }}
        >
          My Account
        </Button>
        <Button
          type="text"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          style={{
            width: '100%',
            color: '#000000',
            textAlign: 'left',
            padding: '8px 16px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            background: 'transparent',
            transition: 'all 0.2s'
          }}
        >
          Logout
        </Button>
      </div>
    </>
  )

  if (loading || authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return null // Will redirect in useEffect
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {showSidebar && (
        <Sider
          collapsible={!showDrawer}
          collapsed={collapsed}
          onCollapse={toggleCollapsed}
          theme="light"
          width={200}
          style={{
            background: '#fafafa',
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            overflow: 'auto',
            zIndex: 100,
            border: 'none',
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.05)'
          }}
          trigger={null}
        >
          <SidebarContent />
        </Sider>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title={null}
        placement="left"
        closable={false}
        open={drawerVisible && showDrawer}
        onClose={() => setDrawerVisible(false)}
        styles={{ body: { padding: 0, background: '#fafafa' } }}
        style={{ zIndex: 1000 }}
        width={280}
      >
        <SidebarContent />
      </Drawer>

      <Layout style={{
        marginLeft: showSidebar ? (collapsed ? 80 : 200) : 0,
        transition: 'margin-left 0.2s'
      }}>
        <Header style={{
          background: '#fff',
          padding: screens.xs ? '0 16px' : '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 99,
          height: screens.xs ? '56px' : '64px'
        }}>
          <Space>
            {showDrawer && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ fontSize: '18px' }}
              />
            )}
            {showSidebar && (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={toggleCollapsed}
                style={{ fontSize: '16px', padding: '4px 8px' }}
              />
            )}
          </Space>

          <Space>
            <Text strong style={{ fontSize: screens.xs ? '14px' : '16px' }}>
              Admin Panel
            </Text>
            <Dropdown menu={{
              items: [
                {
                  key: 'profile',
                  label: 'Profile',
                  onClick: () => navigate('/account/details')
                },
                {
                  key: 'shop',
                  label: 'Back to Shop',
                  onClick: () => navigate('/account/dashboard')
                }
              ]
            }}>
              <Avatar size={screens.xs ? 24 : 32} icon={<UserOutlined />} style={{ cursor: 'pointer' }} />
            </Dropdown>
          </Space>
        </Header>

        <Content style={{
          margin: screens.xs ? '8px' : '24px',
          background: '#fff',
          padding: screens.xs ? '16px' : '24px',
          borderRadius: '8px',
          minHeight: 'calc(100vh - 80px)'
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default AdminLayout
